
import { useState, useEffect } from 'react';
import { Plus, Search, Tag as TagIcon, X, Edit, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

type Tag = {
  id: string;
  name: string;
  category: string;
  count: number;
};

const TagsPage = () => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagCategory, setNewTagCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagCategory, setEditTagCategory] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Fetch tags from Supabase
  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching tags:', error);
        toast.error('Failed to load tags');
        throw error;
      }

      return data as Tag[];
    },
  });

  // Create new tag mutation
  const createTagMutation = useMutation({
    mutationFn: async (tag: { name: string; category: string }) => {
      const { data, error } = await supabase
        .from('tags')
        .insert([
          {
            name: tag.name,
            category: tag.category || 'uncategorized',
            count: 0,
          },
        ])
        .select('*')
        .single();

      if (error) {
        console.error('Error creating tag:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag created successfully');
      setNewTagName('');
      setNewTagCategory('');
      setDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create tag: ${error.message}`);
    },
  });

  // Update tag mutation
  const updateTagMutation = useMutation({
    mutationFn: async (tag: { id: string; name: string; category: string }) => {
      const { data, error } = await supabase
        .from('tags')
        .update({
          name: tag.name,
          category: tag.category || 'uncategorized',
        })
        .eq('id', tag.id)
        .select('*')
        .single();

      if (error) {
        console.error('Error updating tag:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag updated successfully');
      setEditingTag(null);
      setEditTagName('');
      setEditTagCategory('');
      setEditDialogOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to update tag: ${error.message}`);
    },
  });

  // Delete tag mutation
  const deleteTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting tag:', error);
        throw error;
      }

      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete tag: ${error.message}`);
    },
  });

  // Filter tags by search term
  const filteredTags = tags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tag.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group tags by category
  const groupedTags = filteredTags.reduce<Record<string, Tag[]>>(
    (groups, tag) => {
      const category = tag.category || 'uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(tag);
      return groups;
    },
    {}
  );

  // Handle new tag creation
  const handleCreateTag = () => {
    if (newTagName.trim()) {
      createTagMutation.mutate({
        name: newTagName.trim(),
        category: newTagCategory.trim() || 'uncategorized',
      });
    } else {
      toast.error('Tag name is required');
    }
  };

  // Handle tag editing
  const handleEditTag = (tag: Tag) => {
    setEditingTag(tag);
    setEditTagName(tag.name);
    setEditTagCategory(tag.category);
    setEditDialogOpen(true);
  };

  // Handle tag update
  const handleUpdateTag = () => {
    if (editingTag && editTagName.trim()) {
      updateTagMutation.mutate({
        id: editingTag.id,
        name: editTagName.trim(),
        category: editTagCategory.trim() || 'uncategorized',
      });
    } else {
      toast.error('Tag name is required');
    }
  };

  // Handle tag deletion
  const handleDeleteTag = (id: string) => {
    deleteTagMutation.mutate(id);
  };

  if (!isAuthenticated()) {
    return (
      <Card className="glass-card">
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Please log in to manage tags.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tags</h1>
          <p className="text-muted-foreground">
            Manage tags for categorizing your content
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Tag
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Tag</DialogTitle>
              <DialogDescription>
                Add a new tag to organize your travel content
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="tag-name">Tag Name</Label>
                <Input
                  id="tag-name"
                  placeholder="e.g., Mountain Hiking"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tag-category">Category (optional)</Label>
                <Input
                  id="tag-category"
                  placeholder="e.g., activity"
                  value={newTagCategory}
                  onChange={(e) => setNewTagCategory(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Categories help organize similar tags together
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTag}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Tag Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
            <DialogDescription>
              Update the tag name and category
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-tag-name">Tag Name</Label>
              <Input
                id="edit-tag-name"
                placeholder="e.g., Mountain Hiking"
                value={editTagName}
                onChange={(e) => setEditTagName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tag-category">Category</Label>
              <Input
                id="edit-tag-category"
                placeholder="e.g., activity"
                value={editTagCategory}
                onChange={(e) => setEditTagCategory(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTag}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Tags</CardTitle>
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search tags..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              <p className="mt-2 text-muted-foreground">Loading tags...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.keys(groupedTags).length > 0 ? (
                Object.entries(groupedTags).map(([category, categoryTags]) => (
                  <div key={category} className="space-y-2">
                    <h3 className="text-lg font-semibold capitalize mb-3">
                      {category}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {categoryTags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center glass-card py-2 px-3 rounded-full text-sm"
                        >
                          <TagIcon className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                          <span className="mr-1.5">{tag.name}</span>
                          <Badge variant="secondary" className="text-xs mr-2">
                            {tag.count}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 ml-1 hover:bg-blue-500/10 hover:text-blue-600 rounded-full"
                            onClick={() => handleEditTag(tag)}
                          >
                            <Edit className="h-3 w-3" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 ml-1 hover:bg-destructive/10 hover:text-destructive rounded-full"
                            onClick={() => handleDeleteTag(tag.id)}
                          >
                            <X className="h-3 w-3" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground">
                    No tags found. Create a new tag or adjust your search.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TagsPage;
